import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { get, post, setToken } from './lib/api';
import type { Profile } from './lib/types';

interface AuthValue {
  user: Profile | null;
  needsSetup: boolean;
  loading: boolean;
  login: (userId: number, pin: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<Profile | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const status = await get<{ needsSetup: boolean }>('/api/auth/status');
      setNeedsSetup(status.needsSetup);
      if (status.needsSetup) {
        setUser(null);
        return;
      }
      const me = await get<{ user: Profile }>('/api/auth/me');
      setUser(me.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (userId: number, pin: string) => {
      const res = await post<{ user: Profile; token: string }>('/api/auth/login', { userId, pin });
      setToken(res.token);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await post('/api/auth/logout').catch(() => undefined);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, needsSetup, loading, login, logout, refresh }),
    [user, needsSetup, loading, login, logout, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
